import { createFileRoute } from '@tanstack/react-router';
import { DollarSign, Check } from "lucide-react";
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/finops')({
  component: FinOpsUseCasePage,
  head: () => ({
    meta: [
      { title: 'FinOps & Cost Optimization | Matricks' },
      { name: 'description', content: 'Track and optimize AI and API spending across your organization. Set budgets, get alerts, and reduce waste with comprehensive cost analytics.' },
    ],
  }),
});

const challenges = [
  "No visibility into AI/LLM spending by team or project",
  "Unexpected bills from runaway API usage",
  "Inability to allocate costs to business units",
  "Waste from inefficient model usage patterns",
  "Manual processes for budget tracking",
];

const solutions = [
  {
    iconName: "BarChart3",
    title: "Cost Dashboards",
    description: "Real-time visibility into spending across providers, teams, and projects.",
  },
  {
    iconName: "AlertTriangle",
    title: "Budget Alerts",
    description: "Set budgets and get notified before costs exceed limits.",
  },
  {
    iconName: "TrendingDown",
    title: "Optimization Insights",
    description: "Identify waste and get recommendations to reduce costs.",
  },
  {
    iconName: "Users",
    title: "Cost Allocation",
    description: "Attribute costs to teams, projects, or cost centers for accurate chargeback.",
  },
];

const savings = [
  { metric: "20-40%", label: "AI cost reduction" },
  { metric: "90%", label: "Faster cost attribution" },
  { metric: "100%", label: "Budget visibility" },
  { metric: "Zero", label: "Surprise bills" },
];

const faqItems = [
  {
    question: "How does Matricks track costs?",
    answer: "Every API and AI request flowing through Matricks is tracked with token counts, latency, and associated costs. We calculate costs based on provider pricing and aggregate by team, project, or any custom dimension.",
  },
  {
    question: "Can I set different budgets for different teams?",
    answer: "Yes, create hierarchical budgets at the organization, team, and project level. Each level can have its own limits and alerts. Spending is automatically attributed based on API keys and user context.",
  },
  {
    question: "How do you help reduce AI costs?",
    answer: "We identify optimization opportunities: routing to cheaper models when appropriate, caching similar queries, deduplicating requests, and highlighting inefficient usage patterns. Most organizations see 20-40% savings.",
  },
];

function FinOpsUseCasePage() {
  return (
    <div>
      <ContentHeader
        title="FinOps & Cost Optimization"
        description="Get complete visibility and control over your API and AI spending. Track costs in real-time, set budgets, and optimize usage to reduce waste."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Use Cases", href: "/use-cases" },
          { label: "FinOps" },
        ]}
        badge="Use Case"
      />

      {/* Challenges */}
      <ContentSection
        id="challenges"
        title="The Challenge"
        description="AI and API costs can spiral out of control without proper visibility and governance."
      >
        <div className="space-y-4">
          {challenges.map((challenge, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <DollarSign className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span>{challenge}</span>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Solution */}
      <ContentSection
        id="solution"
        title="The Matricks Solution"
        background="muted"
      >
        <FeatureShowcase features={solutions} columns={2} variant="cards" />
      </ContentSection>

      {/* Savings */}
      <ContentSection
        id="savings"
        title="Expected Outcomes"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {savings.map((item) => (
            <div key={item.label} className="text-center p-6 rounded-xl border bg-card">
              <div className="text-3xl font-bold text-primary">{item.metric}</div>
              <div className="text-sm text-muted-foreground mt-2">{item.label}</div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Features */}
      <ContentSection
        id="features"
        title="Key Capabilities"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Cost Tracking</h3>
            <ul className="mt-4 space-y-2">
              {[
                "Real-time cost monitoring",
                "Breakdown by provider, model, team",
                "Historical trend analysis",
                "Cost forecasting",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Budget Management</h3>
            <ul className="mt-4 space-y-2">
              {[
                "Hierarchical budgets",
                "Threshold alerts",
                "Spending caps",
                "Approval workflows",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Optimization</h3>
            <ul className="mt-4 space-y-2">
              {[
                "Model routing recommendations",
                "Caching suggestions",
                "Waste identification",
                "Usage patterns analysis",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Reporting</h3>
            <ul className="mt-4 space-y-2">
              {[
                "Scheduled cost reports",
                "Custom dashboards",
                "Export to finance tools",
                "Chargeback reports",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Take Control of Your AI Spending"
        description="Start tracking and optimizing your API and AI costs today."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/contact" }}
        background="primary"
      />
    </div>
  );
}
